import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import { ToastrService } from 'ngx-toastr';
import { parseString } from 'xml2js';
import { PapaParseService, PapaParseConfig } from 'ngx-papaparse';

import { Report } from './shared/report.model';
import { saveAs } from 'file-saver';
import { environment } from '../environments/environment';
import { FlexFileError } from './shared/errors/FlexFileError';
import * as JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';

import { WBSAllocationError } from './shared/errors/WBSAllocationError';
import { CategoryError } from './shared/errors/CategoryError';
import { LookupError } from './shared/errors/LookupError';
import { MismatchError } from './shared/errors/MismatchError';
import { OrderOrLotAllocationError } from './shared/errors/OrderOrLotAllocationError';
import { EndItemAllocationError } from './shared/errors/EndItemAllocationError';
import { UnitOrSubunitAllocationError } from './shared/errors/UnitOrSublotAllocationError';
import { WBSNonleafError } from './shared/errors/WBSNonleafError';
import { MismatchedWBSError } from './shared/errors/MismatchedWBSError';
import { OverlappingReportCalDatesError } from './shared/errors/OverlappingReportCalDatesError';
import { InvalidReportCalDateError } from './shared/errors/InvalidReportCalDateError';
import { InvalidReportCalRangeError } from './shared/errors/InvalidReportCalRangeError';

@Injectable()
/**
 * The ReportDataService serves several purposes:
 * 1) Share Report data model across components in the application
 * 2) Surface an Objservable Subject that broadcasts when a file has been uploaded.  
 * Subcribers depend on this notification for things like enabling tabs and buttons that should be
 * disabled until we have a file ready for work
 * 3) Save a working file to JSON output
 * 4) Load a working file from JSON saved on disk
 */
export class ReportDataService {
  report: Report;
  initSubject: Subject<boolean> = new Subject();
  templateSubject: Subject<string> = new Subject();
  reloadSubject: Subject<string> = new Subject();
  errorCountSubject: Subject<number> = new Subject();
  rawFilesSubject: BehaviorSubject<Array<any>> = new BehaviorSubject([]);
  selectedWorksheet: String;
  environment: any;
  rawData: [any];
  start: number;
  end: number;
  totalTime: number;
  rows = 0;
  currentError: FlexFileError; //keep track of error state so we can open appropriate modal

  orderOrLotIDMapped = false;
  orderOrLotNameMapped = false;
  CLIN_IDMapped = false;
  CLIN_NameMapped = false;
  endItemIDMapped = false;
  endItemNameMapped = false;
  functionalCategoryIDMapped = false;
  functionalCategoryNameMapped = false;
  functionalOverheadCategoryIDMapped = false;
  functionalOverheadCategoryNameMapped = false;
  accountIDMapped = false;
  accountNameMapped = false;
  firstUnitNumberMapped = false;
  lastUnitNumberMapped = false;

  constructor(private spinner: Ng4LoadingSpinnerService, private toastr: ToastrService, private csvParser: PapaParseService) {
    this.environment = environment;
    this.report = new Report();
    // default to not initialized
    this.initSubject.next(false);
    // default to metadata tab
    this.templateSubject.next(this.environment.METADATA);
    //start w/ 0 errors
    this.errorCountSubject.next(0);
  }

  // allow components to set the value of init, which will be broadcast out to listeners
  setInit(val: boolean) {
    //TEMP - skip the intro wizard for testing
    this.initSubject.next(val);
  }

  // get a handle to the report data model singleton
  getReport() {
    return this.report;
  }

  // Allow listening components to subscribe for init updates
  getInit(): Subject<boolean> {
    return this.initSubject;
  }

  // Allow listeners to get template URI updates
  getTemplate(): Subject<string> {
    return this.templateSubject;
  }

  // Reload broadcast...will let listening components trigger reload of grid component from updated model data
  getReload(): Subject<string> {
    return this.reloadSubject;
  }

  getErrorCount(): Subject<number> {
    return this.errorCountSubject;
  }

  getRawFileData(): BehaviorSubject<Array<any>> {
    return this.rawFilesSubject;
  }

  // Export a file to JSON and save it to disk
  save() {
    //Using jszip + filesaver
    const zip = new JSZip();
    zip.file('FileType.txt', 'CSDR_COST_HOUR_REPORT/1.0_DRAFT20180105');
    zip.file('ReportConfiguration.json', JSON.stringify(this.report.ReportConfiguration.exportObject()));
    zip.file('ReportMetadata.json', JSON.stringify(this.report.ReportMetadata.exportObject()));
    zip.file('OrdersOrLots.json', JSON.stringify(this.report.OrdersOrLots.export()));
    zip.file('CLINs.json', JSON.stringify(this.report.CLINS.export()));
    zip.file('EndItems.json', JSON.stringify(this.report.EndItems.export()));
    zip.file('WBS.json', JSON.stringify(this.report.WBS.export()));
    zip.file('Accounts.json', JSON.stringify(this.report.Accounts.export()));
    zip.file('FunctionalCategories.json', JSON.stringify(this.report.FunctionalCategories.export()));
    zip.file('FunctionalOverheadCategories.json', JSON.stringify(this.report.FunctionalOverheadCategories.export()));
    zip.file('UnitsOrSublots.json', JSON.stringify(this.report.UnitsOrSublots.export()));
    zip.file('CostHourDefinitions.json', JSON.stringify(this.report.CostHourDefinitions.export()));
    zip.file('ReportingCalendar.json', JSON.stringify(this.report.ReportingCalendar.export()));
    zip.file('SummaryCostData.json', JSON.stringify(this.report.SummaryCostData.export()));
    zip.file('ActualCostHourData.json', JSON.stringify(this.report.ActualCostHourData.export()));
    zip.file('ForecastAtCompletionCostHourData.json', JSON.stringify(this.report.ForecastAtCompletionCostHourData.export()));
    zip.file('OrderOrLotAllocationMethods.json', JSON.stringify(this.report.OrderOrLotAllocationMethods.export()));
    zip.file('OrderOrLotAllocationComponents.json', JSON.stringify(this.report.OrderOrLotAllocationComponents.export()));
    zip.file('EndItemAllocationMethods.json', JSON.stringify(this.report.EndItemAllocationMethods.export()));
    zip.file('EndItemAllocationComponents.json', JSON.stringify(this.report.EndItemAllocationComponents.export()));
    zip.file('WBSAllocationMethods.json', JSON.stringify(this.report.WBSAllocationMethods.export()));
    zip.file('WBSAllocationComponents.json', JSON.stringify(this.report.WBSAllocationComponents.export()));
    zip.file('UnitOrSublotAllocationMethods.json', JSON.stringify(this.report.UnitOrSublotAllocationMethods.export()));
    zip.file('UnitOrSublotAllocationComponents.json', JSON.stringify(this.report.UnitOrSublotAllocationComponents.export()));
    zip.file('SummaryRemarks.json', JSON.stringify(this.report.SummaryRemarks.export()));
    zip.file('WBSElementRemarks.json', JSON.stringify(this.report.WBSElementRemarks.export()));
    zip.file('WBSDictionaryDefinitions.json', JSON.stringify(this.report.WBSDictionaryDefinitions.export()));
    zip.file('CostHourTagDefinitions.json', JSON.stringify(this.report.CostHourTagDefinitions.export()));
    this.spinner.show();
    let theSpinner = this.spinner;
    zip.generateAsync({ type: 'blob', compression: 'DEFLATE' }).then(function (blob) {
      saveAs(blob, 'FlexFile.zip');
      theSpinner.hide();
    });

    /*  This is using filesaverjs, which crashes browser on biggish files...
    let blob = new Blob([this.report.toJSON()], {
      type: 'application/json'
    });
    console.log(this.report.toJSON());
    saveAs(blob, 'FlexFile.json');
    */
  }

  load(fileData) {
    this.report.validateAndLoadJSON(fileData);
  }

  setSelectedWorksheet(worksheet) {
    this.selectedWorksheet = worksheet;
    let templateURI = null;
    if (this.selectedWorksheet === this.environment.METADATA) {
      templateURI = '/assets/templates/FlexFile -Q Metadata Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.ORDER_OR_LOTS) {
      templateURI = '/assets/templates/FlexFile -Q Orders or Lots Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.END_ITEMS) {
      templateURI = '/assets/templates/FlexFile End Items Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.WBS) {
      templateURI = '/assets/templates/FlexFile -Q WBS Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.QUANTITIES_AT_COMPLETION) {
      templateURI = '/assets/templates/FlexFile -Q Quantities At Completion Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.QUANTITIES_TO_DATE) {
      templateURI = '/assets/templates/FlexFile -Q Quantities To Date Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.PRODUCTION_SEQUENCE) {
      templateURI = '/assets/templates/FlexFile -Q Production Sequence Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.SUMMARY_REMARKS) {
      templateURI = '/assets/templates/FlexFile -Q Summary Remarks Template.xlsx';
    }
    if (this.selectedWorksheet === this.environment.WBS_ELEMENT_REMARKS) {
      templateURI = '/assets/templates/FlexFile -Q WBS Element Remarks Template.xlsx';
    }
    this.templateSubject.next(templateURI);
  }

  importFromExcel(evt: any) {
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) throw new Error('Cannot use multiple files');
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, {
        type: 'binary'
      });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      //get the error count before load
      const preloadErrorCount = this.report.getErrorCount();
      /* save data */
      let data = (XLSX.utils.sheet_to_json(ws, {
        raw: true,
        header: 1
      }));
      if (this.selectedWorksheet === this.environment.METADATA) {
        this.report.loadMetadataFromExcel(data);
      }
      if (this.selectedWorksheet === this.environment.ORDER_OF_LOTS) {
        this.report.loadOrderOrLotsFromExcel(data);
      }
      if (this.selectedWorksheet === this.environment.END_ITEMS) {
        this.report.loadEndItemsFromExcel(data);
      }
      if (this.selectedWorksheet === this.environment.WBS) {
        this.report.loadWBSFromExcel(data);
      }
      if (this.selectedWorksheet === this.environment.SUMMARY_REMARKS) {
        this.report.loadSummaryRemarksFromExcel(data);
      }
      if (this.selectedWorksheet === this.environment.WBS_ELEMENT_REMARKS) {
        this.report.loadWBSElementRemarksFromExcel(data);
      }
      this.reloadSubject.next('X');
      //get the error count before load
      const postloadErrorCount = this.report.getErrorCount();
      //UI Notification
      this.toastr.success('Parsed data available for review and edit in the worksheet below', 'Excel Template Loaded');
      if (postloadErrorCount !== preloadErrorCount) {
        this.toastr.error('We found some problems with your Excel data.  Check out the "Errors" tab for more detail.', 'Excel Data Errors');
      }
    };
    reader.readAsBinaryString(target.files[0]);
  }

  getSelectedWorksheet() {
    return this.selectedWorksheet;
  }

  pushError(error: FlexFileError) {
    this.report.pushError(error);
    this.errorCountSubject.next(this.report.getErrorCount());
  }

  popError(type: string) {
    this.report.popError(type);
    this.errorCountSubject.next(this.report.getErrorCount());
  }

  parsePlan(event) {
    let fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      let file: File = fileList[0];
      let fs: FileReader = new FileReader();
      fs.onloadend = (e) => {
        let xmlData = fs.result;
        parseString(xmlData, {
          ignoreAttrs: true,
          explicitArray: false
        }, (err, result) => {
          //console.log(JSON.stringify(result));

          //Grab Metadata
          const metadata = result['csdr:Document'].Metadata;
          this.report.ReportMetadata.data[14].Value = metadata.ApprovedPlanNumber;
          this.report.ReportMetadata.data[20].Value = metadata.ContractNumber;
          this.report.ReportMetadata.data[5].Value = metadata.ContractPlanType;
          this.report.ReportMetadata.data[7].Value = metadata.ContractorNameAndAddress.Division;
          this.report.ReportMetadata.data[6].Value = metadata.ContractorNameAndAddress.PerformingOrganization;
          this.report.ReportMetadata.data[3].Value = metadata.PhaseOrMilestone;
          this.report.ReportMetadata.data[32].Value = metadata.PointOfContact.EmailAddress;
          this.report.ReportMetadata.data[29].Value = metadata.PointOfContact.NameAndAddress;
          this.report.ReportMetadata.data[31].Value = metadata.PointOfContact.TelephoneNumber;
          this.report.ReportMetadata.data[4].Value = metadata.PrimeMissionProduct;
          this.report.ReportMetadata.data[2].Value = metadata.ProgramName;
          // this.report.ReportMetadata.data[25].Value = metadata.SubmissionType; //Report Cycle ID - TO-DO: This comes from somewhere else
          this.report.ReportMetadata.data[10].Value =
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Address']['x1:City'];
          this.report.ReportMetadata.data[10].Value =
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Address']['x1:Line1'] +
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Address']['x1:Line2'];
          this.report.ReportMetadata.data[11].Value =
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Address']['x1:State'];
          this.report.ReportMetadata.data[12].Value =
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Address']['x1:ZipCode'];
          this.report.ReportMetadata.data[6].Value =
            metadata['x1:PerformingOrganizationStructuredNameAndAddress']['x1:Name'];

          // Grab WBS
          const wbs = result['csdr:Document'].ReportingStructure.Elements.Element;
          // root of the WBS
          this.report.WBS.data.push({});
          // recurse through the tree of children
          this.walkWBS(wbs, 1, '');
          //notify listeners that file is available
          this.setInit(true);
          //UI Notification
          this.toastr.success('Click on the "Worksheet View" tab to review and edit data', 'Co-Plan Loaded');
        });
      };
      fs.readAsText(file);
    }
  }

  parseJSON(event) {
    let fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      let file: File = fileList[0];
      let fs: FileReader = new FileReader();
      fs.onloadend = (e) => {
        let loadedObj = JSON.parse(fs.result);
        console.log(loadedObj);
        this.load(fs.result);
        this.setInit(true);
        this.toastr.success('Click on the "Worksheet View" tab to review and edit data after completing start wizard', 'Existing FlexFile Loaded');
      };
      fs.readAsText(file);
    }
  }

  walkWBS(currentNode, levelCounter, parentId) {
    // push info to report WBS object
    this.report.WBS.data.push({
      ID: currentNode.ID,
      Name: currentNode.Name,
      Level: levelCounter,
      ParentID: parentId,
      isLeaf: !currentNode.ChildElements || !currentNode.ChildElements.Element || !currentNode.ChildElements.Element.length
    });
    // console.log(currentNode.ID);
    if (!currentNode.hasOwnProperty('ChildElements')) {
      levelCounter--;
      return;
    }
    levelCounter++;
    let children = currentNode.ChildElements.Element;
    for (let i = 0; i < children.length; i++) {
      this.walkWBS(children[i], levelCounter, currentNode.ID);
    }
  }

  parseCSV(event) {
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const start = window.performance.now();
      const file: File = fileList[0];
      return new Promise((resolve, reject) => {
        this.csvParser.parse(file, {
          fastMode: true,
          skipEmptyLines: true,
          complete: (results, f) => {
            const end = window.performance.now();
            let rawHeaders = [];
            //push headers to un-mapped
            for (let i = 0; i < results.data[0].length; i++) {
              rawHeaders.push({
                name: results.data[0][i],
                index: i,
                fileName: file.name
              });
            }
            //we assigned indices to headers, so no longer need them in order...let's alphabatize them for easier mapping
            rawHeaders.sort(function (a, b) {
              var textA = a.name.toUpperCase();
              var textB = b.name.toUpperCase();
              return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
            });

            const rows = results.data.length;
            this.report.setRawData(file.name, rawHeaders, results.data, rows);
            this.rawFilesSubject.next(this.report.getRawData());
            const totalTime = ((end - start) / 1000);
            this.toastr.success(
              'Loaded ' + rows + ' of raw data from ' + file.name + ' in ' + totalTime + ' seconds. Click on the "Mapping" tab to map columns',
              'Raw CSV File Loaded');
            resolve();
          }
        });
      });
    }
  }

  //called once all mapping completes.  Generates unique list for all mapped data and jams into appropriate data structure
  finalizeMaps() {
    this.report.ReportingCalendar.storeUnique(true);
    this.handleNameAndIDVariations();
    this.report.CostHourDefinitions.storeUnique(true);
    //make reporting calendar behave according to DID rules...no gaps in timeline, in chronological order
    this.fixReportingCalendar();
    //make sure that WBS from plan matches WBS from CSV
    //and make sure CSV data in cost hour def is only reported against leaf nodes in WBS
    let reportToNonLeafWBS = false;
    let wbsMismatch = false;
    let nonLeafData = [];
    let mismatchedWBS = []; 
    //get a unique list of WBS codes from cost hour def
    let uniqueCSVWBSCodes = this.report.CostHourDefinitions.getUniqueCol('WBSElementID');
    let uniquePlanWBSMap = new Map();
    for(let i=0; i < this.report.WBS.data.length; i++) {
      uniquePlanWBSMap.set(this.report.WBS.data[i].ID, this.report.WBS.data[i]);
    }
    for (let i = 0; i < uniqueCSVWBSCodes.length; i++) {
      if (!uniquePlanWBSMap.has(uniqueCSVWBSCodes[i])) {
        wbsMismatch = true;
        mismatchedWBS.push(uniqueCSVWBSCodes[i]);
        continue;
      }
      if (!uniquePlanWBSMap.get(uniqueCSVWBSCodes[i]).isLeaf) {
        reportToNonLeafWBS = true;
        nonLeafData.push(uniqueCSVWBSCodes[i]);
      }
    }
    if(nonLeafData.length > 0) {
      this.toastr.error('One or more items reported in your data are recorded against non-leaf WBS Plan IDs.  Check out the "Errors" tab for more detail.', 'Non-leaf WBS Data');
      this.pushError(new WBSNonleafError(this, nonLeafData));
    }
    if(mismatchedWBS.length > 0) {
      this.toastr.error('One or more items reported in your data don\'t have a WBS ID in Plan.  Check out the "Errors" tab for more detail.', 'Mismatched WBS');
      this.pushError(new MismatchedWBSError(this, mismatchedWBS));
    }

    //check for lookup errors...TO-DO: Should we build mapping to allow user to fix these?
    let nonRecurring = this.report.CostHourDefinitions.getUniqueCol('NonrecurringOrRecurringID');
    let nonRecurringErrors = [];
    let nonRecurringError = false;
    for (let i=0; i < nonRecurring.length; i++) {
      if (!this.report.isEnumValid(nonRecurring[i], 'NONRECURRING_RECURRING')) {
        nonRecurringErrors.push(nonRecurring[i]);
        nonRecurringError = true;
      }
    }
    if(nonRecurringError) {
        this.toastr.error('Enumeration Error - Found unexpected Non-Recurring or Recurring value(s).  Check out the "Errors" tab for more detail.', 'Enumeration Error');
        this.pushError(new LookupError(this, 'Enumeration Error - Found unexpected Non-Recurring Or Recurring Value(s).', nonRecurringErrors));
    }

    //check to make sure we have either StandardCat or DetailedStandard cat, based on report config
    if (this.report.ReportConfiguration.data[0].Value === true) {
      //we should be using detailed standard cats...make sure they didn't make standard cat
      if (this.report.CostHourDefinitions.getUniqueCol('StandardCategoryID').length > 0) {
        //raise an error
        this.toastr.error('Your report is configured to use Detailed Standard Categories, but you provided Standard Categories.  Check out the "Errors" tab for more detail.', 'Category Error');
        this.pushError(new CategoryError(this, 'Your report is configured to use Detailed Categories, but you provided Standard Categories', null));
      } else {
        //make sure lookups are valid
        let cats = this.report.CostHourDefinitions.getUniqueCol('DetailedStandardCategoryID');
        let catErrors = [];
        let catError = false;
        for (let i=0; i < cats.length; i++) {
          if (!this.report.isEnumValid(cats[i], 'DETAILED_STANDARD_CAT')) {
            catErrors.push(cats[i]);
            catError = true;
          }
        }
        if(catError) {
          this.toastr.error('Enumeration Error - Found unexpected Detailed Standard Category value(s).  Check out the "Errors" tab for more detail.', 'Enumeration Error');
          this.pushError(new LookupError(this, 'Enumeration Error - Found unexpected Detailed Standard Category Value(s).', catErrors));
        }
      }
    } else {
      //we should be using standard cats...make sure they didn't make detailed standard cat
      if (this.report.CostHourDefinitions.getUniqueCol('DetailedStandardCategoryID').length > 0) {
        //raise an error
        this.toastr.error('Your report is configured to use Standard Categories, but you provided Detailed Categories.  Check out the "Errors" tab for more detail.', 'Category Error');
        this.pushError(new CategoryError(this, 'Your report is configured to use Standard Categories, but you provided Detailed Categories', null));
      } else {
        //make sure lookups are valid
        let cats = this.report.CostHourDefinitions.getUniqueCol('StandardCategoryID');
        let catErrors = [];
        let catError = false;
        for (let i=0; i < cats.length; i++) {
          if (!this.report.isEnumValid(cats[i], 'STANDARD_CAT')) {
            catErrors.push(cats[i]);
            catError = true;
          }
        }
        if(catError) {
          this.toastr.error('Enumeration Error - Found unexpected Standard Category value(s).  Check out the "Errors" tab for more detail.', 'Enumeration Error');
          this.pushError(new LookupError(this, 'Enumeration Error - Found unexpected Standard Category Value(s).', catErrors));
        }
      }
    }
    

    //do mapping for Actual cost hour data
    //generate a hash for costHourDefs and substitute IDs into actualCostHourData by looping over raw
    const costHourMap = [];
    for (let i = 0; i < this.report.CostHourDefinitions.data.length; i++) {
      //need a copy since we're gonna delete ID
      let data = Object.assign({}, this.report.CostHourDefinitions.data[i]);
      let currentID = this.report.CostHourDefinitions.data[i].ID;
      delete data.ID;
      costHourMap[JSON.stringify(data)] = currentID;
    }

    //check for ID/Name mismatches
    const accountIDMap = new Map();
    const accountNameMap = new Map();
    const firstUnitMap = new Map();
    const lastUnitMap = new Map();
    const orderLotIDMap = new Map();
    const orderLotNameMap = new Map();
    let hasAccountIDError = false;
    let hasAccountNameError = false;
    let hasFirstUnitError = false;
    let hasLastUnitError = false;
    let hasOrderLotIDError = false;
    let hasOrderLotNameError = false;

//XXX
    for (let i = 0; i < this.report.Accounts.data.length; i++) {
      let data = this.report.Accounts.data[i];
      if(this.hasMismatch(accountIDMap, data.ID, data.Name)) {
        hasAccountIDError = true;
      }
      if(this.hasMismatch(accountNameMap, data.AccountName, data.AccountID)) {
        hasAccountNameError = true;
      }
    }

    for (let i = 0; i < this.report.OrdersOrLots.data.length; i++) {
      let data = this.report.OrdersOrLots.data[i];
      if(this.hasMismatch(orderLotIDMap, data.OrderLotID, data.OrderLotName)) {
        hasOrderLotIDError = true;
      }
      if(this.hasMismatch(orderLotNameMap, data.OrderLotName, data.OrderLotID)) {
        hasOrderLotNameError = true;
      }
    }

    for (let i = 0; i < this.report.CostHourDefinitions.data.length; i++) {
      let data = this.report.CostHourDefinitions.data[i];
      if(this.hasMismatch(firstUnitMap, data.FirstUnitNumber, data.LastUnitNumber)) {
        hasFirstUnitError = true;
      }
      if(this.hasMismatch(lastUnitMap, data.LastUnitNumber, data.FirstUnitNumber)) {
        hasLastUnitError = true;
      }
    }

   const accountIDErrors = this.pullErrors(accountIDMap, "Found ID: ", " with multiple names: ");
   const accountNameErrors = this.pullErrors(accountNameMap, "Found Name: ", " with multiple IDs: ");
   const orderLotIDErrors = this.pullErrors(orderLotIDMap, "Found ID: ", " with multiple names: ");
   const orderLotNameErrors = this.pullErrors(orderLotNameMap, "Found Name: ", " with multiple IDs: ");
   const firstUnitErrors = this.pullErrors(firstUnitMap, "Found First Unit: ", " with multiple Last Units: ");
   const lastUnitErrors = this.pullErrors(lastUnitMap, "Found Last Unit: ", " with multiple First Units: ");
   if (accountIDErrors.length > 0) {
      this.toastr.error('Found one or more account IDs with multiple associated names - Account ID/Account Name pairs must be unique.  Check out the "Errors" tab for more detail.', 'Account Error');
      this.pushError(new MismatchError(this, 'Account Error - Duplicate Account ID/Name pairs.', accountIDErrors));
    }
    if(accountNameErrors.length > 0) {
      this.toastr.error('Found one or more account Names with multiple associated IDs - Account ID/Account Name pairs must be unique.  Check out the "Errors" tab for more detail.', 'Account Error');
      this.pushError(new MismatchError(this, 'Account Error - Duplicate Account Name/ID pairs.', accountNameErrors));
    }

    if(orderLotIDErrors.length > 0) {
      this.toastr.error('Found one or more Order/Lot IDs with multiple associated names - Order/Lot ID / Oder/Lot Name pairs must be unique.  Check out the "Errors" tab for more detail.', 'Order/Lot Error');
      this.pushError(new MismatchError(this, 'Order/Lot Error - Duplicate Order/Lot ID/Name pairs.', orderLotIDErrors));
    }
    if(orderLotNameErrors.length > 0) {
      this.toastr.error('Found one or more Order/Lot Names with multiple associated IDs - Order/Lot ID/Account Name pairs must be unique.  Check out the "Errors" tab for more detail.', 'Order/Lot Error');
      this.pushError(new MismatchError(this, 'Order/Lot Error - Duplicate Order/Lot Name/ID pairs.', orderLotNameErrors));
    }

    if(firstUnitErrors.length > 0) {
      this.toastr.error('Found one or more First Unit Numbers with multiple associated Last Unit Numbers - First Unit/Last Unit pairs must be unique.  Check out the "Errors" tab for more detail.', 'First Unit Error');
      this.pushError(new MismatchError(this, 'First Unit Error - Duplicate First Unit/Last Unit pairs.', firstUnitErrors));
    }
    if(lastUnitErrors.length > 0) {
      this.toastr.error('Found one or more Last Unit Numbers with multiple associated First Unit Numbers - Last Unit/First Unit pairs must be unique.  Check out the "Errors" tab for more detail.', 'Last Unit Error');
      this.pushError(new MismatchError(this, 'Last Unit Error - Duplicate Last Unit/First Unit pairs.', lastUnitErrors));
    }
    const reportingCalendarMap = [];
    for (let i = 0; i < this.report.ReportingCalendar.data.length; i++) {
      let data = Object.assign({}, this.report.ReportingCalendar.data[i]);
      let currentID = data.ID;
      delete data.ID;
      reportingCalendarMap[JSON.stringify(data)] = currentID;
    }

    let uniqueCostMap = new Map();
    //loop over all the cost hour data and roll up $$$ and hours that are for same 
    //cost hour def and reporting period
    for (let i = 0; i < this.report.ActualCostHourData.data.length; i++) {
      let data = this.report.ActualCostHourData.data[i];
      data.CostHourDefinitionID = costHourMap[data.costHourDefKey];
      data.ReportingPeriodID = reportingCalendarMap[data.reportingCalKey];
      let costKey = data.CostHourDefinitionID + '_' + data.ReportingPeriodID;
      if(uniqueCostMap.get(costKey) == null) {
        uniqueCostMap.set(costKey, {
          CostHourDefinitionID: data.CostHourDefinitionID, 
          ReportingPeriodID: data.ReportingPeriodID,
          Value_Dollars: +data.Value_Dollars,
          Value_Hours: +data.Value_Hours
        });
      }
      else {
        let currentItem = uniqueCostMap.get(costKey);
        currentItem.Value_Dollars += +data.Value_Dollars;
        currentItem.Value_Hours += +data.Value_Hours;
      }
    }

    //now dump the map into Report ActualCostHourData tables
    this.report.ActualCostHourData.data = null;
    this.report.ActualCostHourData.data = Array.from(uniqueCostMap.values());

    //now that costs are accumulated, put the IDs in cost hour defs where needed
    this.insertIDsForNames();

    //populate SummaryCostData w/ Order or Lot ID
    this.report.SummaryCostData.pushDataFromCol(['OrderOrLotID'], this.report.OrdersOrLots.data, ['ID']);

    //populate SummaryRemarks w/ Order or Lot ID
    this.report.SummaryRemarks.pushDataFromCol(['OrderOrLotID'], this.report.OrdersOrLots.data, ['ID']);

    //populate WBS Definitions w/ WBS Element ID
    this.report.WBSDictionaryDefinitions.pushDataFromCol(['WBSElementID'], this.report.WBS.data, ['ID']);

    //populate/clean up Unit Or Sublot Data. 
    //OK, this looks a little weird because we're not remapping any columns names....
    //We're just grabbing the fields we want from the CostHourDef table and jamming them the UnitsOrSublots report table data
    if(this.firstUnitNumberMapped && this.lastUnitNumberMapped) {
      this.report.UnitsOrSublots.data = this.report.CostHourDefinitions.transformColData(['FirstUnitNumber', 'LastUnitNumber', 'EndItemID', 'OrderOrLotID'], ['FirstUnitNumber', 'LastUnitNumber', 'EndItemID', 'OrderOrLotID']);
      this.report.UnitsOrSublots.storeUnique(true);
      //now jam the IDs into cost hour def table
      //hash by key of firstitem, lastitem, OrderOrLotId, EndItemId
      let map = [];
      for (let i = 0; i < this.report.UnitsOrSublots.data.length; i++) {
        let currentItem = this.report.UnitsOrSublots.data[i];
        map['' + currentItem.FirstUnitNumber + currentItem.LastUnitNumber + currentItem.OrderOrLotID + currentItem.EndItemID] = currentItem.ID;
      }

      //now put IDs in the Cost Hour Def Map
      //and while we're at it, save having to re-loop array and store dollar totals for various potential allocation methods
      for (let i = 0; i < this.report.CostHourDefinitions.data.length; i++) {
        let currentItem = this.report.CostHourDefinitions.data[i];
        let currentKey = currentItem.FirstUnitNumber + currentItem.LastUnitNumber + currentItem.OrderOrLotID + currentItem.EndItemID;
        currentItem.UnitOrSublotID = map['' + currentKey];
      }
    }

    //populate WBS Element Remarks w/ Order or Lot ID and WBS Element ID
    //store unique WBS/OrderOrLotID combos so we can populate WBS Element Remarks data later
    //empty out orderLot/EndItem IDs if first & Last unit are mapped
    let wbsOrderLotMap = {};
    for (let i = 0; i < this.report.CostHourDefinitions.data.length; i++) {
      let currentItem = this.report.CostHourDefinitions.data[i];
      wbsOrderLotMap[currentItem.WBSElementID + currentItem.OrderOrLotID] = { WBSElementID: currentItem.WBSElementID, OrderOrLotID: currentItem.OrderOrLotID };
      if(this.firstUnitNumberMapped && this.lastUnitNumberMapped) {
        currentItem.OrderOrLotID = '';
        currentItem.EndItemID = '';
      }
    }
    let wbsOrderLotArray = [];
    for (var key in wbsOrderLotMap) {
      wbsOrderLotArray.push(wbsOrderLotMap[key]);
    }

    this.report.WBSElementRemarks.pushDataFromCol(['WBSElementID', 'OrderOrLotID'], wbsOrderLotArray, ['WBSElementID', 'OrderOrLotID']);

    //now get rid of costHourDefKey and reportingCalKey and LastUnitNumber in ActualCostHourData
    for (let i = 0; i < this.report.ActualCostHourData.data.length; i++) {
      //Check to make sure setting to undefined works and JSON.Stringify ignores on export
      let currentItem = this.report.ActualCostHourData.data[i];
      //currentItem.costHourDefKey = undefined;
      //currentItem.reportingCalKey = undefined;
      //currentItem.uniqueKey = undefined;
      delete currentItem.costHourDefKey;
      delete currentItem.reportingCalKey;
      delete currentItem.uniqueKey;
    }

    // raise allocation errors to user if need be
    if (this.report.CostHourDefinitions.requiresAllocation('WBSElementID', 'WBSAllocationMethodID', 'WBSElementID')) {
      this.toastr.error('We need to create WBS Allocations for some of your Cost Definition Data.  Check out the "Errors" tab for more detail.', 'Allocation Needed');
      this.pushError(new WBSAllocationError(this));
    }
    if (this.report.CostHourDefinitions.requiresAllocation('OrderOrLotID', 'OrderOrLotAllocationMethodID', 'UnitOrSublotID')) {
      this.toastr.error('We need to create Order or Lot Allocations for some of your Cost Definition Data.  Check out the "Errors" tab for more detail.', 'Allocation Needed');
      this.pushError(new OrderOrLotAllocationError(this));
    }
    if (this.report.CostHourDefinitions.requiresAllocation('EndItemID', 'EndItemAllocationMethodID', 'UnitOrSublotID')) {
      this.toastr.error('We need to create End Item Allocations for some of your Cost Definition Data.  Check out the "Errors" tab for more detail.', 'Allocation Needed');
      this.pushError(new EndItemAllocationError(this));
    }
    if ((this.firstUnitNumberMapped && this.lastUnitNumberMapped) && this.report.CostHourDefinitions.requiresAllocation('UnitOrSublotID', 'UnitOrSublotAllocationMethodID', 'UnitOrSublotID')) {
      this.toastr.error('We need to create Unit or Sublot Allocations for some of your Cost Definition Data.  Check out the "Errors" tab for more detail.', 'Allocation Needed');
      this.pushError(new UnitOrSubunitAllocationError(this));
    }

    //get rid of temporary columsn
    for(let i=0; i < this.report.WBS.data.length; i++) {
      delete this.report.WBS.data[i].isLeaf;
    }
    this.report.WBS.removeEmpty('ID');
    this.report.WBSDictionaryDefinitions.removeEmpty('WBSElementID');
  }

  handleNameAndIDVariations() {
    //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.CLIN_IDMapped) {
      let clins = this.report.CostHourDefinitions.transformColData(['CLIN_Name'], ['Name']);
      this.report.CLINS.storeUniqueCol(clins, true);
    } else if (this.CLIN_IDMapped && !this.CLIN_NameMapped) { //have an ID, but no name, just copy ID over to name
      let clins = this.report.CostHourDefinitions.transformColData(['CLIN_ID'], ['ID']);
      this.report.CLINS.storeUniqueCol(clins, false);
      this.report.CLINS.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let clins = this.report.CostHourDefinitions.transformColData(['CLIN_ID', 'CLIN_Name'], ['ID', 'Name']);
      this.report.CLINS.insertData(clins);
      this.report.CLINS.storeUnique(false);
    }

    //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.functionalCategoryIDMapped) {
      let functionalCat = this.report.CostHourDefinitions.transformColData(['FunctionalCategoryName'], ['Name']);
      this.report.FunctionalCategories.storeUniqueCol(functionalCat, true);
    } else if (this.functionalCategoryIDMapped && !this.functionalCategoryNameMapped) { //have an ID, but no name, just copy ID over to name
      let functionalCat = this.report.CostHourDefinitions.transformColData(['FunctionalCategoryID'], ['ID']);
      this.report.FunctionalCategories.storeUniqueCol(functionalCat, false);
      this.report.FunctionalCategories.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let functionalCat = this.report.CostHourDefinitions.transformColData(['FunctionalCategoryID', 'FunctionalCategoryName'], ['ID', 'Name']);
      this.report.FunctionalCategories.insertData(functionalCat);
      this.report.FunctionalCategories.storeUnique(false);
    }

        //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.functionalOverheadCategoryIDMapped) {
      let functionalOverheadCat = this.report.CostHourDefinitions.transformColData(['FunctionalOverheadCategoryName'], ['Name']);
      this.report.FunctionalOverheadCategories.storeUniqueCol(functionalOverheadCat, true);
    } else if (this.functionalOverheadCategoryIDMapped && !this.functionalOverheadCategoryNameMapped) { //have an ID, but no name, just copy ID over to name
      let functionalOverheadCat = this.report.CostHourDefinitions.transformColData(['FunctionalOverheadCategoryID'], ['ID']);
      this.report.FunctionalOverheadCategories.storeUniqueCol(functionalOverheadCat, false);
      this.report.FunctionalOverheadCategories.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let functionalOverheadCat = this.report.CostHourDefinitions.transformColData(['FunctionalOverheadCategoryID', 'FunctionalOverheadCategoryName'], ['ID', 'Name']);
      this.report.FunctionalOverheadCategories.insertData(functionalOverheadCat);
      this.report.FunctionalOverheadCategories.storeUnique(false);
    }

    //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.accountIDMapped) {
      let accounts = this.report.CostHourDefinitions.transformColData(['AccountName'], ['Name']);
      this.report.Accounts.storeUniqueCol(accounts, true);
    } else if (this.accountIDMapped && !this.accountNameMapped) { //have an ID, but no name, just copy ID over to name
      let accounts = this.report.CostHourDefinitions.transformColData(['AccountID'], ['ID']);
      this.report.Accounts.storeUniqueCol(accounts, false);
      this.report.Accounts.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let accounts = this.report.CostHourDefinitions.transformColData(['AccountID', 'AccountName'], ['ID', 'Name']);
      this.report.Accounts.insertData(accounts);
      this.report.Accounts.storeUnique(false);
    }

    //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.endItemIDMapped) {
      let endItems = this.report.CostHourDefinitions.transformColData(['EndItemName'], ['Name']);
      this.report.EndItems.storeUniqueCol(endItems, true);
    } else if (this.endItemIDMapped && !this.endItemNameMapped) { //have an ID, but no name, just copy ID over to name
      let endItems = this.report.CostHourDefinitions.transformColData(['EndItemID'], ['ID']);
      this.report.EndItems.storeUniqueCol(endItems, false);
      this.report.EndItems.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let endItems = this.report.CostHourDefinitions.transformColData(['EndItemID', 'EndItemName'], ['ID', 'Name']);
      this.report.EndItems.insertData(endItems);
      this.report.EndItems.storeUnique(false);
    }

    //if we don't have CLIN ID, we must have CLIN Name, autogen ID
    if(!this.orderOrLotIDMapped) {
      let orderLots = this.report.CostHourDefinitions.transformColData(['OrderOrLotName'], ['Name']);
      this.report.OrdersOrLots.storeUniqueCol(orderLots, true);
    } else if (this.orderOrLotIDMapped && !this.orderOrLotNameMapped) { //have an ID, but no name, just copy ID over to name
      let orderLots = this.report.CostHourDefinitions.transformColData(['OrderOrLotID'], ['ID']);
      this.report.OrdersOrLots.storeUniqueCol(orderLots, false);
      this.report.OrdersOrLots.copyInternalData(['ID'], ['Name']);
    } else { //we have both Name and ID
      //grab ID/Name pairs from Cost Hour Defs and store in CLINS
      let orderLots = this.report.CostHourDefinitions.transformColData(['OrderOrLotID', 'OrderOrLotName'], ['ID', 'Name']);
      this.report.OrdersOrLots.insertData(orderLots);
      this.report.OrdersOrLots.storeUnique(false);
    }
 }

 insertIDsForNames() {
    let CLINNameIDMap, functionalCatNameIDMap, functionalOverheadCatNameIDMap, accountNameIDMap, endItemNameIDMap, orderLotNameIDMap = [];
    CLINNameIDMap = this.makeNameIdMap(this.report.CLINS);
    functionalCatNameIDMap = this.makeNameIdMap(this.report.FunctionalCategories);
    functionalOverheadCatNameIDMap = this.makeNameIdMap(this.report.FunctionalOverheadCategories);
    accountNameIDMap = this.makeNameIdMap(this.report.Accounts);
    endItemNameIDMap = this.makeNameIdMap(this.report.EndItems);
    orderLotNameIDMap = this.makeNameIdMap(this.report.OrdersOrLots);
    for(let i=0; i < this.report.CostHourDefinitions.data.length; i++) {
      if(!this.CLIN_IDMapped) {
        this.report.CostHourDefinitions.data[i].CLIN_ID = CLINNameIDMap[this.report.CostHourDefinitions.data[i].CLIN_Name];
      }
      if(!this.accountIDMapped) {
        this.report.CostHourDefinitions.data[i].AccountID = accountNameIDMap[this.report.CostHourDefinitions.data[i].AccountName];
      }
      if(!this.functionalCategoryIDMapped) {
        this.report.CostHourDefinitions.data[i].FunctionalCategoryID = functionalCatNameIDMap[this.report.CostHourDefinitions.data[i].FunctionalCategoryName];
      }
      if(!this.functionalOverheadCategoryIDMapped) {
        this.report.CostHourDefinitions.data[i].FunctionalOverheadCategoryID = functionalOverheadCatNameIDMap[this.report.CostHourDefinitions.data[i].FunctionalOverheadCategoryName];
      }
      if(!this.endItemIDMapped) {
        this.report.CostHourDefinitions.data[i].EndItemID = endItemNameIDMap[this.report.CostHourDefinitions.data[i].EndItemName];
      }
      if(!this.orderOrLotIDMapped) {
        this.report.CostHourDefinitions.data[i].OrderOrLotID = orderLotNameIDMap[this.report.CostHourDefinitions.data[i].OrderOrLotName];
      }
    }
  }

  makeNameIdMap(wrapper) {
    let map = [];
    for(let i=0; i < wrapper.data.length; i++) {
      map[wrapper.data[i].Name] = wrapper.data[i].ID;
    }
    return map;
  }




  pullErrors(sourceMap, prefix, suffix) {
    const errors = [];
    Array.from(sourceMap.keys()).forEach(id => {
      const currentValues = sourceMap.get(id);
      if(currentValues.length > 1) {
        let errorString = '';
        for(let i=0; i < currentValues.length; i++) {
          if(i !== 0) {
            errorString += ', ';
          }
          errorString += currentValues[i];
        }
        errors.push(prefix + id + suffix + errorString);
      }
    });
    return errors;
  }

  hasMismatch(keyMap, currentKey, currentValue) {
      const values = keyMap.get(currentKey);
      let foundValue = false;

      if (values != null) {
        for(let i=0; i < values.length; i++) {
          if(values[i] === currentValue) {
              foundValue = true;
              break;
          }
        }
        if(!foundValue) {
          values.push(currentValue);
        }
      } else {
        keyMap.set(currentKey, [currentValue]);
      }
      return !foundValue;
  }



  //make reporting calendar behave according to DID rules...no gaps in timeline, in chronological order
  fixReportingCalendar() {
    let cal = this.report.ReportingCalendar.data;
    //sort data based on startdate
    cal.sort(function (a, b) {
      return moment(new Date(a.StartDate)).isBefore(new Date(b.StartDate)) ? -1 : moment(new Date(a.StartDate)).isAfter(new Date(b.StartDate)) ? 1 : 0;
    });
    //now loop through and validate:
    //if gaps in timeline, add new entry to fill gap
    //if end date isn't >= start date, log error
    //re-assign IDs so sequence is valid per DID

    //we might need to add stuff, let's just jam it in new array and then assign new object back
    //when done
    let newCal = [];
    let sequence = 1;
    let hasOverlappingDates = false;
    let hasInvalidRange = false;
    let hasInvalidDate = false;
    let overlappingDates = [];
    let invalidRanges = [];
    let invalidDates = [];

    for (let i = 0; i < (cal.length - 1); i++) {
      let current = cal[i];
      let next = cal[i + 1];
      let currentStart = moment(current.StartDate);
      let currentEnd = moment(current.EndDate);
      if(!currentStart.isValid() || !currentEnd.isValid()){
        hasInvalidDate = true;
        invalidDates.push('Start Date: ' + current.StartDate + ' End Date: ' + current.endDate);
        continue;
      }
      let nextStart = moment(next.StartDate);
      if (currentEnd.isBefore(currentStart)) {
        hasInvalidRange = true;
        invalidRanges.push('Start Date: ' + current.StartDate + ' End Date: ' + current.endDate);
        continue;
      }
      //set id of current and push to new list
      current.ID = sequence;
      sequence++;
      newCal.push(current);

      if (currentEnd.isSameOrAfter(nextStart)) {
        //no bueno....ranges can't overlap
        hasOverlappingDates = true;
        overlappingDates.push('Start Date: ' + current.StartDate + ' End Date: ' + current.endDate);
        console.log('TO-DO: Handle overlapping dates error...' + current.EndDate + ' after ' + next.StartDate);
        continue;
      }

      //DID says next must start 1 day after current....let's seee if is
      currentEnd.add(1, 'd');
      if (!currentEnd.isSame(nextStart, 'day')) {
        //need to create a new interval to fill gap
        newCal.push({
          ID: sequence,
          StartDate: currentEnd.format('MM/DD/YYYY'),
          EndDate: nextStart.subtract(1, 'd').format('MM/DD/YYYY')
        });
        sequence++;
      }
    }
    if(hasOverlappingDates) {
      this.toastr.error('One or more Reporting Calendar ranges overlap.  Check out the "Errors" tab for more detail.', 'Overlapping Reporting Calendar Ranges');
      this.pushError(new OverlappingReportCalDatesError(this, overlappingDates));
    }
    if(hasInvalidRange) {
      this.toastr.error('One or more Reporting Calendar ranges are invalid.  Check out the "Errors" tab for more detail.', 'Invalid Reporting Calendar Ranges');
      this.pushError(new InvalidReportCalRangeError(this, invalidRanges));
    }
    if(hasInvalidDate) {
      this.toastr.error('One or more Reporting Calendar dates are invalid.  Check out the "Errors" tab for more detail.', 'Invalid Reporting Calendar Dates');
      this.pushError(new InvalidReportCalDateError(this, invalidDates));
    }
    //set the sequence on last element in the original array and push to new list
    cal[cal.length - 1].ID = sequence;
    newCal.push(cal[cal.length - 1]);
    this.report.ReportingCalendar.data = newCal;
  }

  addAllocationDollars(dollarMap, key, value) {
    if (dollarMap[key] == null) {
      dollarMap[key] = value;
    }
    else {
      dollarMap[key] += value;
    }
  }
  mapData(mappingInfo) {
    //get the raw file data we want to map
    const rawData = this.report.getRawDataFile(mappingInfo.mappedFromData.fileName);
    if (mappingInfo.table === environment.COST_HOUR_DEFINITIONS) {
      this.report.CostHourDefinitions.mapData(mappingInfo.mappedFromData.index, rawData.rawData, mappingInfo.id);
    }
    if (mappingInfo.table === environment.ACCOUNTS) {
      this.report.Accounts.mapData(mappingInfo.mappedFromData.index, rawData.rawData, mappingInfo.id);
    }
    if (mappingInfo.table === environment.REPORTING_CALENDAR) {
      this.report.ReportingCalendar.mapData(mappingInfo.mappedFromData.index, rawData.rawData, mappingInfo.id);
    }
    if (mappingInfo.table === environment.ACTUAL_COST_HOUR_DATA) {
      this.report.ActualCostHourData.mapActualCostHourData(mappingInfo.mappedFromData.index, rawData.rawData, mappingInfo.id, this.report.CostHourDefinitions.data, this.report.ReportingCalendar.data);
    }
    if (mappingInfo.table === environment.FORECAST_AT_COMPLETION_COST_HOUR) {
      this.report.ForecastAtCompletionCostHourData.mapData(mappingInfo.mappedFromData.index, rawData.rawData, mappingInfo.id);
    }
  }


  isCostDefinitionMapped() {
    let mapped = this.report.CostHourDefinitions.mappedCols;
    return (mapped.get('OrderOrLotID') && mapped.get('CLIN_ID') && mapped.get('EndItemID')
      && mapped.get('WBSElementID') && mapped.get('AccountID') && mapped.get('NonrecurringOrRecurringID')
      && mapped.get('FunctionalCategoryID') && mapped.get('FirstUnitNumber') && mapped.get('LastUnitNumber'));

  }

  //there are several fields in costhourdef where user can provide some combo of ID/Name pairs...we need to track so
  //we can handle logic correctly when we finalize mapping
  storeNameIDVariations() {
    this.orderOrLotIDMapped = false;
    this.orderOrLotNameMapped = false;
    this.CLIN_IDMapped = false;
    this.CLIN_NameMapped = false;
    this.endItemIDMapped = false;
    this.endItemNameMapped = false;
    this.functionalCategoryIDMapped = false;
    this.functionalCategoryNameMapped = false;
    this.functionalOverheadCategoryIDMapped = false;
    this.functionalOverheadCategoryNameMapped = false;
    this.accountIDMapped = false;
    this.accountNameMapped = false;
    this.firstUnitNumberMapped = false;
    this.lastUnitNumberMapped = false;

    let mapped = this.report.CostHourDefinitions.mappedCols;
    if(mapped.get('OrderOrLotID')) { this.orderOrLotIDMapped = true;}
    if(mapped.get('OrderOrLotName')) { this.orderOrLotNameMapped = true;}
    if(mapped.get('CLIN_ID')) { this.CLIN_IDMapped = true;}
    if(mapped.get('CLIN_Name')) { this.CLIN_NameMapped = true;}
    if(mapped.get('EndItemID')) { this.endItemIDMapped = true;}
    if(mapped.get('EndItemName')) { this.endItemNameMapped = true;}
    if(mapped.get('FunctionalCategoryID')) { this.functionalCategoryIDMapped = true;}
    if(mapped.get('FunctionalCategoryName')) { this.functionalCategoryNameMapped = true;}
    if(mapped.get('FunctionalOverheadCategoryID')) { this.functionalOverheadCategoryIDMapped = true;}
    if(mapped.get('FunctionalOverheadCategoryName')) { this.functionalOverheadCategoryNameMapped = true;}
    if(mapped.get('AccountID')) { this.accountIDMapped = true;}
    if(mapped.get('AccountName')) { this.accountNameMapped = true;}
    if(mapped.get('FirstUnitNumber')) { this.firstUnitNumberMapped = true;}
    if(mapped.get('LastUnitNumber')) { this.lastUnitNumberMapped = true;}
  }

  isReportingCalMapped() {
    let mapped = this.report.ReportingCalendar.mappedCols;
    return (mapped.get('StartDate') && mapped.get('EndDate'));
  }

  isActualCostMapped() {
    let mapped = this.report.ActualCostHourData.mappedCols;
    return (mapped.get('Value_Dollars') && mapped.get('Value_Hours'));
  }

}



// WEBPACK FOOTER //
// ./src/app/report-data.service.ts